import React, { useRef, useEffect, useMemo, useState } from "react";
import { RootState, AppDispatch } from "../../../redux/store";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { GoogleMap, InfoWindow, LoadScript } from "@react-google-maps/api";
import {
  changeMapTab,
  getGoogleMapApiKey,
  getMapGeoJson,
  getMapGeoJsonStylesOnly,
  selectCurrentMapQuery,
} from "../../../redux/mapSlice";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import { getMapResult } from "../../../redux/mapSlice";
import {
  selectCurrentTab,
  setMsrSelections,
  setQuerySubType,
} from "../../../redux/queryBuilderSlice";
import Loading from "../../../components/Loading";
import LoadingOverlay from "../../../components/LoadingOverlay";
import { selectMapQuery } from "./mapSelectors";
import { showNotification } from "../../../redux/notificationSlice";

const ALLOW_AUTO_ZOOM_AXIS = ["Census Tracts"];
function includesMatch(sourceArray: any, checkArray: any) {
  return checkArray.some((checkValue: any) =>
    sourceArray.some((sourceValue: any) => sourceValue.includes(checkValue)),
  );
}
const mapColorProps = {
  mouseout: {
    fillColor: "#b5d8f3",
    fillOpacity: 0.6,
    strokeColor: "#808080",
    strokeOpacity: 0.6,
    strokeWeight: 1,
  },
  mouseover: {
    fillColor: "#b5d8f3",
    fillOpacity: 0.6,
    strokeColor: "#808080",
    strokeOpacity: 0.6,
    strokeWeight: 1,
  },
  mouseup: {
    fillColor: "#55a8e4",
    fillOpacity: 0.6,
    strokeColor: "#808080",
    strokeOpacity: 0.6,
    strokeWeight: 1,
  },
  mousedown: {
    fillColor: "#0079d1",
    fillOpacity: 0.6,
    strokeColor: "#808080",
    strokeOpacity: 0.6,
    strokeWeight: 1,
  },
  filtered: {
    fillColor: "#003b8f",
    fillOpacity: 0.6,
    strokeColor: "#808080",
    strokeOpacity: 0.6,
    strokeWeight: 1,
  },
};
const defaultMapProps = {
  center: {
    lat: 37.6,
    lng: -95.665,
  },
  zoom: 3,
  outlineWeight: 2,
  strokeColor: "#000000",
};

const getObjectKeys = function (object: any) {
  return Object.keys(object);
};

const buildMapSelectUrl = function (fieldProperties: any) {
  let mapSelectURL = "STDG/STATE_MVR/STATE_MVR";
  if (fieldProperties) {
    mapSelectURL =
      fieldProperties["geo.layer.brand"] +
      "/" +
      fieldProperties["geo.layer.name"] +
      "/" +
      fieldProperties["geo.layer.name"];
  }
  return mapSelectURL;
};

const getCodesByFields = (filters: any) => {
  let codesByField: any = {};
  if (!filters) {
    return codesByField;
  }
  for (const f of filters) {
    if (!codesByField[f.field]) {
      codesByField[f.field] = {};
    }
    if (!f.members) {
      continue;
    }
    for (const mf of f.members) {
      if (!codesByField[f.field].all) {
        codesByField[f.field].all = [];
      }
      if (mf.properties?.geoCode) {
        codesByField[f.field].all = [
          ...codesByField[f.field].all,
          mf.properties?.geoCode,
        ];
      }
      if (!codesByField[f.field].filtered) {
        codesByField[f.field].filtered = [];
      }
      if (f.memberFilters) {
        let found = f.memberFilters.find((ff: any) => {
          return ff === mf.filter_id;
        });
        if (found) {
          if (mf.properties?.geoCode) {
            codesByField[f.field].filtered = [
              ...codesByField[f.field].filtered,
              mf.properties?.geoCode,
            ];
          }
        }
      }
    }
  }
  return codesByField;
};

export default function MapContainer({
  selectedMeasure,
  selectedMeasureField,
  subType,
  currentTabIndex,
}: {
  selectedMeasure: string | null;
  selectedMeasureField: string | null;
  subType: string;
  currentTabIndex: number;
}) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const dispatch: AppDispatch = useDispatch();
  const currTabData = useSelector(selectCurrentTab);
  const currMapData = useSelector((state: any) =>
    selectMapQuery(state, currentTabIndex),
  );
  const flattenedResults = useSelector(
    (state: RootState) => currTabData.results,
  );
  const query = useSelector((state: RootState) => currTabData.query);
  const isSourceHistory = useSelector(
    (state: RootState) => currTabData.isSourceHistory,
  );
  const queryMode = useSelector((state: RootState) => currTabData.queryMode);
  const isLoadQueryMode = useSelector(
    (state: RootState) => currTabData.isLoadQueryMode,
  );
  const querySubType = useSelector(
    (state: RootState) => currTabData.querySubType,
  );

  const [toolTipHoverLabel, setToolTipHoverLabel] = useState<string>("");

  useEffect(() => {
    dispatch(changeMapTab(currentTabIndex));
  }, [currentTabIndex]);
  const {
    geoJson,
    apiKey,
    loading: isLoading,
    result: mapResults,
    isNewQuery,
  } = currMapData || {};

  const [selectedMap, setSelectedMap] = useState<string>(
    querySubType || "thematic",
  );
  const [loadingMap, setLoadingMap] = React.useState(false);

  const [toolTip, setToolTip] = React.useState({
    show: false,
    label: "",
    value: "0",
    position: { lat: 0, lng: 0 },
  });

  const handleMapChange = (newValue: string) => {
    dispatch(setQuerySubType({ type: newValue, currentTabIndex }));

    setSelectedMap(newValue);
  };
  useEffect(() => {
    setSelectedMap(querySubType || "thematic");
  }, [querySubType]);
  const checkIsMeasureAttr = (member: string) => {
    return member.split(".")[0] && member.split(".")[0] == "[Measures]"
      ? true
      : false;
  };

  const getLastBrackateValue = (str: string) => {
    if (typeof str !== "string") return "";

    const matches = str.match(/\[(.*?)\]/g);
    if (!matches || matches.length === 0) return "";

    const last = matches.pop();
    if (!last) return "";
    return last.replace(/^\[|\]$/g, "");
  };

  useEffect(() => {
    if (selectedMap) {
      let measures: any = [];
      mapResults?.result.headers.forEach((head: any, index: any) => {
        if (head.children && head.children.length > 0) {
          const findLastChild = (node: any): any =>
            node.children?.length ? findLastChild(node.children.at(-1)) : node;

          if (measures.length > 0) {
            return;
          } else {
            let myChild = findLastChild(head.children[0]);
            let myNodes: any = [];
            let fields = myChild.field.split("|");
            let levels = myChild.levelUniqueName.split(",");

            myNodes = levels.map((lvl: any, index: number) => {
              let newNode = lvl + ".[" + fields[index] + "]";
              let flag = false;
              mapResults?.query.axes.ROWS.forEach((row: any) => {
                if (!checkIsMeasureAttr(newNode)) {
                  flag = true;
                } else {
                  flag = false;
                }
              });

              if (flag) {
                if (!measures.includes(newNode) && !myNodes.includes(newNode)) {
                  return newNode;
                }
              }
            });
            let isMeasureAdded = false;
            mapResults?.query.axes.COLUMNS.forEach((col: any) => {
              if (
                checkIsMeasureAttr(col) &&
                !isMeasureAdded &&
                !measures.includes(col) &&
                !myNodes.includes(col)
              ) {
                isMeasureAdded = true;
                myNodes.push(col);
              }
            });

            myNodes = myNodes.filter((node: any) => node);
            measures = measures.concat(myNodes);
          }
        } else {
          if (index == mapResults?.result?.headers.length - 1) {
            let myNodes: any = [];
            let isMeasureAdded = false;

            mapResults?.query.axes.COLUMNS.forEach((col: any) => {
              if (
                checkIsMeasureAttr(col) &&
                !measures.includes(col) &&
                !myNodes.includes(col) &&
                !isMeasureAdded
              ) {
                myNodes.push(col);
                isMeasureAdded = true;
              }
            });

            myNodes = myNodes.filter((node: any) => node);
            measures = measures.concat(myNodes);
          }
        }
      });
      dispatch(setMsrSelections({ msr: measures, currentTabIndex }));
    }
  }, [mapResults, selectedMap]);

  useEffect(() => {
    if (!apiKey) {
      dispatch(getGoogleMapApiKey(currentTabIndex));
    }
  }, [apiKey, currentTabIndex]);

  const prevTabIndexRef = useRef(currentTabIndex);

  useEffect(() => {
    if (isSourceHistory) return;

    const tabIndexChanged = prevTabIndexRef.current !== currentTabIndex;

    if (tabIndexChanged && mapResults) {
      prevTabIndexRef.current = currentTabIndex;
      return;
    }

    dispatch(
      getMapResult({
        selectedMap,
        currentTabIndex,
      }),
    );

    prevTabIndexRef.current = currentTabIndex;
  }, [
    flattenedResults,
    selectedMap,
    currentTabIndex,
    isSourceHistory,
    isLoadQueryMode,
  ]);

  const findMatchingChild = (
    child: any,
    selectedMeasureField: string,
    selectedMeasure: string,
  ): any => {
    if (
      child.field === selectedMeasureField &&
      child.headerName == selectedMeasure
    ) {
      return child; // Return the matching child
    }
    if (child.children && Array.isArray(child.children)) {
      for (const nestedChild of child.children) {
        const result = findMatchingChild(
          nestedChild,
          selectedMeasureField,
          selectedMeasure,
        );
        if (result) {
          return result;
        }
      }
    }
    return null;
  };

  const geoRequest = useMemo(() => {
    if (!mapResults) return null;

    let mapHeader: any = {};
    let metricHeader: any = {};

    mapResults.result.headers.forEach((item: any) => {
      if (item.properties?.["map.enable"]?.[0] === "Y") {
        mapHeader = item;
      } else if (item.children?.length) {
        const findLastChild = (node: any): any =>
          node.children?.length ? findLastChild(node.children.at(-1)) : node;

        metricHeader = findLastChild(item.children[0]);
      } else {
        metricHeader = item;
      }
    });

    const metricKeyValues: Record<string, any> = {};

    mapResults.result.rows.forEach((row: any) => {
      let val = 0;
      const geoKey = row[mapHeader.field][1]["GeoKey"];
      let isFirst = true;
      mapResults.result.headers.forEach((item: any) => {
        if (!isFirst) {
          return;
        }
        if (item.properties?.["map.enable"]?.[0] === "Y") {
          return;
        } else {
          function findLeafByHeaderName(obj: any, targetHeader: string): any {
            if (!obj.children || obj.children.length === 0) {
              return obj.headerName === targetHeader ? obj : null;
            }

            for (let child of obj.children) {
              const result: any = findLeafByHeaderName(child, targetHeader);
              if (result) return result;
            }

            return null;
          }

          function findMetricHeader(item: any, selectedMeasure: any) {
            const priorityHeaders = [
              selectedMeasure,
              "Vehicle Count",
              "Fleet Count",
            ].filter(Boolean);

            for (let header of priorityHeaders) {
              const result = findLeafByHeaderName(item, header);
              if (result) return result;
            }

            return null;
          }
          if (item.children?.length > 0) {
            const metricHeader = findMetricHeader(item, selectedMeasure);

            if (metricHeader) {
              val = row[metricHeader.field];
              isFirst = false;
              setToolTipHoverLabel(metricHeader.headerName || "Vehicle Count");
            }
          } else {
            val = row[item.field];
            setToolTipHoverLabel(item.headerName || "Vehicle Count");
            isFirst = false;
          }
        }
      });
      metricKeyValues[geoKey] = val;
    });

    return {
      mapType: selectedMap,
      req: buildMapSelectUrl(mapHeader.properties),
      filterCodes: Object.keys(metricKeyValues),
      metricKeyValues,
    };
  }, [mapResults, selectedMap, selectedMeasure, selectedMeasureField]);
  useEffect(() => {
    if (!geoRequest) return;

    const fetchGeoData = async () => {
      try {
        await dispatch(
          getMapGeoJson({ geoReq: geoRequest, currentTabIndex }),
        ).unwrap();
        // zoomToFitAllFeatures();
        // result === { dataObject: ... }
      } catch (err: any) {
        dispatch(
          showNotification({
            message: err?.message || "An unexpected error occurred!",
            duration: 3000,
            isError: true,
          }),
        );
      }
    };

    fetchGeoData();
  }, [geoRequest, currentTabIndex, dispatch]);

  const zoomToFitAllFeatures = () => {
    const bounds = new window.google.maps.LatLngBounds();
    mapRef?.current?.data?.forEach(function (feature: any) {
      feature.getGeometry().forEachLatLng(function (latlng: any) {
        bounds.extend(latlng);
      });
    });
    mapRef?.current?.fitBounds(bounds);
  };

  return (
    <MapView
      mapData={currMapData}
      selectedMap={selectedMap}
      geoRequest={geoRequest}
      query={query}
      setSelectedMap={setSelectedMap}
      toolTipHoverLabel={toolTipHoverLabel}
      selectedMeasure={selectedMeasure}
      selectedMeasureField={selectedMeasureField}
    />
  );
}

type MapViewProps = {
  mapData: any;
  selectedMap: string;
  setSelectedMap: (v: string) => void;
  selectedMeasure: string | null;
  query: any;
  selectedMeasureField: string | null;
  geoRequest: any;
  toolTipHoverLabel: string;
};

const MapView = React.memo(
  function MapView({
    mapData,
    selectedMap,
    setSelectedMap,
    query,
    selectedMeasure,
    selectedMeasureField,
    toolTipHoverLabel,
    geoRequest,
  }: MapViewProps) {
    const { apiKey, geoJson, loading } = mapData || {};

    const mapRef = useRef<google.maps.Map | null>(null);
    const [mapCenter, setMapCenter] = React.useState(defaultMapProps.center);
    const [isMapLoaded, setIsMapLoaded] = React.useState(false);

    const [toolTip, setToolTip] = useState({
      show: false,
      label: "",
      value: "0",
      position: { lat: 0, lng: 0 },
    });

    const loadHandler = (map: google.maps.Map) => {
      mapRef.current = map;
      setIsMapLoaded(true);
    };

    const updateAreaColors = (
      feature: any,
      action: keyof typeof mapColorProps,
    ) => {
      //const fltrCode = geoRequest?.filterCodes;
      const currCode = feature.getProperty("code");
      if (currCode && action === "mouseover") {
        mapRef?.current?.data?.overrideStyle(feature, mapColorProps.filtered);
      } else {
        if (action === "mouseout") {
          mapRef?.current?.data?.revertStyle(feature);
        } else {
          mapRef?.current?.data?.overrideStyle(feature, mapColorProps[action]);
        }
      }
    };
    const styleFeature = (feature: any) => {
      const metricStyle = feature.getProperty("metricStyles");
      if (!metricStyle?.[0]) return {};
      return metricStyle[0].style;
    };

    useEffect(() => {
      let mouseclickListener: google.maps.MapsEventListener;
      let mouseoverListener: google.maps.MapsEventListener;
      let mouseoutListener: google.maps.MapsEventListener;
      let mouseupListener: google.maps.MapsEventListener;
      let mousedownListener: google.maps.MapsEventListener;
      if (geoJson && mapRef?.current) {
        mouseoverListener = mapRef.current?.data.addListener(
          "mouseover",
          mouseoverToRegion,
        );
        mouseupListener = mapRef.current?.data.addListener(
          "mouseup",
          mouseupInToRegion,
        );
        mousedownListener = mapRef.current?.data.addListener(
          "mousedown",
          mousedownInToRegion,
        );
        mouseclickListener = mapRef.current?.data.addListener(
          "click",
          mouseClickRegion,
        );
        mouseoutListener = mapRef.current?.data.addListener(
          "mouseout",
          mouseoutOfRegion,
        );
        if (geoJson.dataObject.features || geoJson.dataObject.dotgeoData) {
          loadMapShapes(geoJson.dataObject);
        }
      }
      return () => {
        if (mouseclickListener) {
          mouseclickListener.remove();
        }
        if (mouseupListener) {
          mouseupListener.remove();
        }
        if (mousedownListener) {
          mousedownListener.remove();
        }
        if (mouseoverListener) {
          mouseoverListener.remove();
        }
        if (mouseoutListener) {
          mouseoutListener.remove();
        }
      };
    }, [geoJson, mapRef]);

    const mouseupInToRegion = (e: any) => {
      if (!e?.feature) return;
      updateAreaColors(e.feature, "mouseup");
    };

    const mousedownInToRegion = (e: any) => {
      if (!e?.feature) return;
      updateAreaColors(e.feature, "mousedown");
    };

    const mouseoverToRegion = (e: any) => {
      if (!e?.feature) return;
      updateAreaColors(e.feature, "mouseover");
      const a = e.feature.getProperty("label") || "";
      const b = e.feature.getProperty("metricStyles")?.[0]?.metric || "";
      const toolTipProps = {
        show: true,
        label: a,
        value: b,
        position: { lat: e.latLng.lat(), lng: e.latLng.lng() },
      };
      setToolTip(toolTipProps);
    };

    const mouseoutOfRegion = (e: any) => {
      if (!e?.feature) return;
      updateAreaColors(e.feature, "mouseout");
      const toolTipProps = {
        ...toolTip,
        show: false,
        label: "",
        value: "",
        position: { lat: 0, lng: 0 },
      };
      setToolTip(toolTipProps);
    };

    const mouseClickRegion = (e: any) => {
      if (!e?.feature) return;
      const c = e.feature.getProperty("code");
      setToolTip({
        ...toolTip,
        show: false,
        position: { lat: 0, lng: 0 },
      });
    };

    const [circles, setCircles] = useState<google.maps.Circle[]>([]);

    const currentBoundsRef = useRef<google.maps.LatLngBounds | null>(null);
    const setGeoCodeToZoom = (geoCodeToZoom: any) => {
      let bounds: google.maps.LatLngBounds | null = null;
      mapRef?.current?.data?.forEach(function (feature: any) {
        if (feature.getProperty("code") === geoCodeToZoom) {
          bounds = new window.google.maps.LatLngBounds();
          feature.getGeometry().forEachLatLng(function (latlng: any) {
            bounds!.extend(latlng);
          });
        }
      });
      if (bounds) {
        // Check if the new bounds are different from the current bounds
        if (
          !currentBoundsRef.current ||
          !currentBoundsRef.current.equals(bounds)
        ) {
          currentBoundsRef.current = bounds;
          mapRef?.current?.fitBounds(bounds);
        }
      } else {
        mapRef?.current?.data?.revertStyle();
        const defaultBounds = new window.google.maps.LatLngBounds();
        defaultBounds.extend(
          new window.google.maps.LatLng(28.920728, -124.415162),
        );
        defaultBounds.extend(
          new window.google.maps.LatLng(45.012912, -71.856577),
        );

        if (
          !currentBoundsRef.current ||
          !currentBoundsRef.current.equals(defaultBounds)
        ) {
          currentBoundsRef.current = defaultBounds;
          mapRef?.current?.fitBounds(defaultBounds);
        }
      }
    };
    const loadMapShapes = (data: any) => {
      mapRef?.current?.data.forEach(function (feature) {
        mapRef?.current?.data.remove(feature);
      });

      circles.forEach((circle: any) => {
        circle.setMap(null);
      });
      circles.length = 0;

      if (selectedMap === "dotdensity") {
        mapRef.current?.data.addGeoJson(data.thematicgeoData);
        createDotLayer(data.dotgeoData);
      } else {
        mapRef?.current?.data?.addGeoJson(data);
      }
      mapRef?.current?.data.setStyle(styleFeature);
      if (includesMatch(query.axes["ROWS"], ALLOW_AUTO_ZOOM_AXIS)) {
        zoomToFitMarkedStates(geoRequest?.filterCodes);
      } else {
        let geoCode = "";
        if (geoRequest?.filterCodes.length == 1) {
          geoCode = geoRequest?.filterCodes[0];
        }
        setGeoCodeToZoom(geoCode);
      }
    };
    const zoomToFitMarkedStates = (filterCodes?: string[]) => {
      const map = mapRef.current;
      if (!map || !filterCodes || filterCodes.length === 0) return;

      const codeSet = new Set(filterCodes);
      const bounds = new google.maps.LatLngBounds();
      let matchedCount = 0;

      map.data.forEach((feature) => {
        const code = feature.getProperty("code") as string | undefined;
        if (!code || !codeSet.has(code)) return;

        matchedCount++;

        const geom = feature.getGeometry();
        if (!geom) return;

        // Traverse any geometry type (Polygon/MultiPolygon/etc.) safely
        geom.forEachLatLng((latLng: google.maps.LatLng) => {
          bounds.extend(latLng);
        });
      });

      // If none of the filter codes matched any feature, do nothing (or fallback)
      if (matchedCount === 0) return;

      map.fitBounds(bounds);

      // If only one state, force a minimum zoom-in (fitBounds sometimes stays too zoomed out)
      if (filterCodes.length === 1) {
        const z = map.getZoom();
        if (typeof z === "number") {
          map.setZoom(Math.max(z, 7)); // adjust 7 as needed
        }
      }
    };
    const createDotLayer = (data: any) => {
      if (!mapRef.current) return;

      const circlesList: any = [];
      data.features.forEach((feature: any) => {
        const [lng, lat] = feature.geometry.coordinates;
        const circle = new google.maps.Circle({
          center: { lat, lng },
          map: mapRef.current,
          fillColor: "green",
          fillOpacity: 1,
          strokeColor: "green",
          strokeWeight: 1,
          strokeOpacity: 1,
          radius: 1000,
        });
        circlesList.push(circle);
      });
      setCircles(circlesList);
    };

    const RenderColorPalette = (data: any) => {
      let metricColors: any = [];
      let metricBins: any = [];
      if (data.data.dataObject.dotgeoData) {
        const cluster =
          data.data.dataObject.dotgeoData.properties.colorBins[0].cluster;
        return (
          <div
            id="legend"
            style={{
              fontFamily: "Arial, sans-serif",
              background: "#fff",
              padding: "10px",
              margin: "10px",
              position: "absolute",
              top: "120px",
              right: "-5px",
            }}
          >
            <div style={{ fontSize: "12px", fontWeight: "bold" }}>Legend</div>
            <div
              style={{
                display: "flex",
                gap: "0px",
                marginLeft: "5px",
                alignItems: "center",
              }}
            >
              <FontAwesomeIcon icon={faCircle} style={{ color: "green" }} />
              <span>1 dot = {cluster} units</span>
            </div>
          </div>
        );
      } else {
        if (data.data.dataObject.properties) {
          metricBins = data.data.dataObject.properties.colorBins[0].bins;
          // Keep only the first and last element
          metricBins = [metricBins[0], metricBins[metricBins.length - 1]];
          data.data.dataObject.properties.colorBins[0].styles.forEach(
            (style: any) => {
              metricColors.push(style.fillColor);
            },
          );
        }

        /*if(isLoading){
		return <div className="right-panel"><Loading show={true} /></div>;
	} */

        return (
          <div
            id="legend"
            style={{
              fontFamily: "Arial, sans-serif",
              background: "#fff",
              padding: "10px",
              margin: "10px",
              position: "absolute",
              top: "120px",
              right: "-5px",
            }}
          >
            <div style={{ fontSize: "12px", fontWeight: "bold" }}>Legend</div>
            <div style={{ display: "flex", gap: "0px", marginLeft: "5px" }}>
              {metricColors.map((item: any) => (
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    backgroundColor: item,
                  }}
                />
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              {metricBins.map((item: any) => (
                <span style={{ fontSize: "9px" }}>{item}</span>
              ))}
            </div>
          </div>
        );
      }
    };
    const mapOptions = {
      //center : new google.maps.LatLng(40, -100),
      zoom: 4,
      scaleControl: true,

      /*zoomControlOptions: {
			position: google.maps.ControlPosition.RIGHT_CENTER
		},
		panControlOptions: {
			position: google.maps.ControlPosition.RIGHT_TOP
		},
		fullscreenControlOptions: {
			position: google.maps.ControlPosition.BOTTOM_LEFT
		},*/
      styles: [
        {
          featureType: "poi",
          stylers: [
            {
              visibility: "off",
            },
          ],
        },
        {
          featureType: "landscape",
          elementType: "geometry",
          stylers: [
            {
              visibility: "on",
            },
            {
              color: "#fcfcfc",
            },
          ],
        },
        {
          featureType: "water",
          elementType: "labels",
          stylers: [
            {
              visibility: "off",
            },
          ],
        },
        {
          featureType: "water",
          elementType: "geometry",
          stylers: [
            {
              visibility: "on",
            },
            {
              hue: "#5f94ff",
            },
            {
              lightness: 60,
            },
          ],
        },
      ],
    };

    const dropdownOptions = [
      { label: "Thematic", value: "thematic" },
      { label: "Dot Density", value: "dotdensity" },
    ];
    return (
      <React.Fragment>
        <div className="map-dropdown">
          <Dropdown
            id="map-dropdown"
            options={[
              { label: "Thematic", value: "thematic" },
              { label: "Dot Density", value: "dotdensity" },
            ]}
            selectedValue={selectedMap}
            onChange={setSelectedMap}
          />
        </div>

        <div style={{ height: 400 }}>
          {apiKey && (
            <LoadScript googleMapsApiKey={apiKey}>
              <GoogleMap
                onLoad={loadHandler}
                center={mapCenter}
                zoom={defaultMapProps.zoom}
                mapContainerStyle={{
                  height: "100%",
                  width: "100%",
                }}
                options={mapOptions}
              >
                {isMapLoaded && geoJson && (
                  <RenderColorPalette data={geoJson}></RenderColorPalette>
                )}
                {toolTip.show && (
                  <InfoWindow position={toolTip.position}>
                    <div>
                      <div>{toolTip.label}</div>
                      <div>
                        {selectedMeasure ?? toolTipHoverLabel}: {toolTip.value}
                      </div>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            </LoadScript>
          )}
        </div>

        <LoadingOverlay show={loading} />
      </React.Fragment>
    );
  },

  (prev, next) =>
    prev.mapData === next.mapData &&
    prev.selectedMap === next.selectedMap &&
    prev.selectedMeasure === next.selectedMeasure &&
    prev.selectedMeasureField === next.selectedMeasureField,
);
